import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { GET_AUDITS } from "../../../../apis/settingsApis/audit-apis/auditApis";
import { GET_FRAMEWORK_LIST } from "../../../../apis/settingsApis/framework-apis/frameworkApis";
import { GET_EXTERNAL_USERS } from "../../../../apis/organization-apis/people-apis/externalUserApis";
import { GET_STAFF_DETAILS } from "../../../../apis/organization-apis/people-apis/peopleApis";
import { AuditStatus, AuditStatusLabel } from "../../../../utils/enums";
import { firstActiveAuditorRowPerId } from "../utils/auditDetailsUtils";

export const useAuditDetails = (auditId) => {
  const { data: auditsData, loading: auditLoading, refetch: refetchAudit } = useQuery(GET_AUDITS, {
    variables: {
      page: 1,
      limit: 1,
      filter: { id: auditId },
    },
    skip: !auditId,
    fetchPolicy: "cache-and-network",
  });

  const { data: frameworksData } = useQuery(GET_FRAMEWORK_LIST, {
    fetchPolicy: "cache-first",
  });

  const { data: externalUsersData } = useQuery(GET_EXTERNAL_USERS, {
    fetchPolicy: "cache-first",
  });

  const { data: employeesData } = useQuery(GET_STAFF_DETAILS, {
    variables: { filter: { in_scope: true } },
    fetchPolicy: "cache-first",
  });

  const audit = auditsData?.getAudits?.data?.[0];
  const frameworks = frameworksData?.getFramework || [];
  const externalUsers = externalUsersData?.getExternalUsers?.data || [];
  const employees = employeesData?.getEmployees?.data || [];

  // Build combined picker options for auditor selection
  const auditorPickerOptions = useMemo(() => {
    const externalOptions = firstActiveAuditorRowPerId(externalUsers).map((u) => ({
      id: String(u.id),
      name: u.name || "",
      email: u.email || "",
      is_internal: false,
    }));

    const internalOptions = employees
      .filter((emp) => emp.employee_data_id && emp.in_scope !== false && !emp.termination_date)
      .map((emp) => ({
        id: String(emp.employee_data_id),
        name: emp.name || emp.work_email?.split("@")[0] || "",
        email: emp.work_email || "",
        is_internal: true,
      }));

    return [...internalOptions, ...externalOptions];
  }, [externalUsers, employees]);

  const processedAudit = useMemo(() => {
    if (!audit) return null;

    const framework = frameworks.find((f) => f.framework_id === audit.framework_id);

    // auditors field now comes directly from API with name/email
    const auditors = audit.auditors || [];
    const auditorNames = auditors.map((a) => a.name).filter(Boolean).join(", ");
    const auditorEmails = auditors.map((a) => a.email).filter(Boolean).join(", ");

    const frameworkDisplay = framework?.framework_description || framework?.framework_name || "N/A";

    return {
      ...audit,
      framework_name: frameworkDisplay,
      auditor_name: auditorNames || (audit.audit_type === "internal" ? "Internal Auditing" : "Not Assigned"),
      auditor_email: auditorEmails,
      status:
        AuditStatusLabel[audit.audit_status || AuditStatus.ACTIVE] || "Active",
    };
  }, [audit, frameworks]);

  return {
    audit,
    frameworks,
    externalUsers,
    employees,
    auditorPickerOptions,
    processedAudit,
    auditLoading,
    refetchAudit,
  };
};
